import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  ArrayInput,
  AutocompleteInput,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  required,
  useDataProvider,
} from "react-admin";
import { Alert, Grid, Typography } from "@mui/material";

import CustomDateInput from "server/components/Fields/CustomDateInput";
import CustomTimeOnlyInput from "server/components/Fields/CustomTimeOnlyInput";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import { PreparatoryClassTypeChoices, WeekDayChoices } from "utils/choices";
import { useNotifyError } from "utils/useNotifyError";
import { validateEnd, validateStart } from "utils/validations";

// Preparatory students all study the one DEOS curriculum in force for the selected academic year, so the
// curriculum is resolved by the backend rather than picked — that also gives us the allowed disciplines.
const PreparatoryScheduleForm = ({ mode }) => {
  const isEdit = mode === "edit";
  const dataProvider = useDataProvider();
  const notifyError = useNotifyError();
  const { setValue } = useFormContext();

  const academicYearId = useWatch({ name: "academicYearId" });
  const [curriculum, setCurriculum] = useState(null);

  useEffect(() => {
    if (!academicYearId) {
      setCurriculum(null);
      return;
    }

    let cancelled = false;

    const loadCurriculum = async () => {
      try {
        const { data } = await dataProvider.customRequest(
          studentRequests.getPreparatoryScheduleCurriculum(academicYearId),
        );

        if (cancelled) return;

        setCurriculum(data);
        setValue("curriculumId", data.curriculumId, { shouldDirty: false });
      } catch (error) {
        if (cancelled) return;

        setCurriculum(null);
        notifyError(error);
      }
    };

    loadCurriculum();

    return () => {
      cancelled = true;
    };
  }, [academicYearId, dataProvider, notifyError, setValue]);

  const disciplineChoices = (curriculum?.disciplines ?? []).map(d => ({
    id: d.disciplineId,
    name: `${d.nameBg}${formatHorarium(d)}`,
  }));

  return (
    <>
      <Grid
        container
        spacing={2}
        px={6}
      >
        <Grid
          item
          xs={12}
          md={5}
        >
          <ReferenceInput
            source="academicYearId"
            reference={studentRequests.getAcademicYearsSelect()}
          >
            <SelectInput
              label="Академична година"
              helperText={false}
              validate={required()}
              disabled={isEdit}
              fullWidth
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={5}
        >
          <ReferenceInput
            source="instructionLanguageId"
            reference={administrationRequests.getInstructionLanguagesSelect()}
          >
            <SelectInput
              label="Език на обучение"
              helperText={false}
              validate={required()}
              disabled={isEdit}
              fullWidth
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={10}
        >
          <TextInput
            source="name"
            label="Наименование"
            validate={required()}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={10}
        >
          {curriculum ? (
            <Typography color="primary">Учебен план: {curriculum.curriculumName}</Typography>
          ) : (
            <Alert severity="info">Изберете академична година, за да се зареди учебният план на ДЕОС.</Alert>
          )}
        </Grid>
      </Grid>

      {curriculum ? (
        <ArrayInput
          label="Дисциплини"
          source="disciplines"
          defaultValue={[{}]}
        >
          <SimpleFormIterator>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
              >
                <AutocompleteInput
                  source="disciplineId"
                  label="Дисциплина"
                  choices={disciplineChoices}
                  validate={required()}
                  fullWidth
                />
              </Grid>
              <Grid
                item
                xs={12}
              >
                <ArrayInput
                  label="Периоди"
                  source="periods"
                  defaultValue={[{}]}
                >
                  <SimpleFormIterator>
                    <PeriodInputs />
                  </SimpleFormIterator>
                </ArrayInput>
              </Grid>
            </Grid>
          </SimpleFormIterator>
        </ArrayInput>
      ) : null}
    </>
  );
};

const PeriodInputs = () => {
  const fromDate = useWatch({ name: "fromDate" });
  const toDate = useWatch({ name: "toDate" });
  const classTypeId = useWatch({ name: "classTypeId" });

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={4}
      >
        <SelectInput
          source="classTypeId"
          label="Вид занятие"
          choices={PreparatoryClassTypeChoices}
          validate={required()}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <CustomDateInput
          source="fromDate"
          label="От дата"
          validate={() => validateStart(fromDate, toDate, required)}
          required
          noTime
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <CustomDateInput
          source="toDate"
          label="До дата"
          validate={() => validateEnd(toDate, fromDate, required)}
          required
          noTime
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={3}
      >
        <CustomTimeOnlyInput
          source="startTime"
          label="Начален час"
          required={required()}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={3}
      >
        <CustomTimeOnlyInput
          source="endTime"
          label="Краен час"
          required={required()}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <SelectArrayInput
          source="weekDays"
          label="Дни от седмицата"
          choices={WeekDayChoices}
          helperText="Оставете празно, за да се провежда всеки ден от периода"
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <ProfessorInput
          source="professorEmployeeId"
          validate={required()}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="roomId"
          reference={administrationRequests.getRoomsSelect()}
          filter={classTypeId ? { classTypes: classTypeId } : {}}
        >
          <AutocompleteInput
            label="Зала"
            optionValue="id"
            optionText={rec => `${rec.roomName} (${rec.roomNumber || ""}) ${rec.buildingName}`}
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            fullWidth
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <StudyGroupField
          labelStyle={{ fontSize: "11px" }}
          label="Пример: 1-5, 8, 11-13. Оставете празно, ако периодът се отнася за всички групи!"
        />
      </Grid>
    </Grid>
  );
};

const formatHorarium = discipline => {
  const parts = [];

  if (discipline.lecturesHorarium) parts.push(`лекции ${discipline.lecturesHorarium} ч.`);
  if (discipline.exercisesHorarium) parts.push(`упражнения ${discipline.exercisesHorarium} ч.`);

  return parts.length ? ` — ${parts.join(", ")}` : "";
};

export default PreparatoryScheduleForm;
